import { useState } from "react";
import type { CreatedUser } from "@reina/shared";
import {
  Button,
  EmptyState,
  Field,
  Modal,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  TableWrap,
  useToast,
} from "../../components";
import { useMe } from "../../api/auth";
import { useCreateUser, useSetUserActive, useUsers } from "../../api/users";
import { errorMessage, formatDateTime } from "../../lib/format";

/** Create-user modal: form first, then the one-time initial password. */
function CreateUserModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const create = useCreateUser();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [created, setCreated] = useState<CreatedUser | null>(null);

  const submit = () => {
    create.mutate(
      { name: name.trim(), email: email.trim() },
      {
        onSuccess: (result) => setCreated(result),
        onError: (error) => toast.push({ message: errorMessage(error), tone: "danger" }),
      },
    );
  };

  if (created) {
    return (
      <Modal
        open
        title="User created"
        sub={created.user.email}
        onClose={onClose}
        footer={
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        }
      >
        <div className="stack">
          <div className="sub">
            Initial password — <strong>shown only this once</strong>; share it securely and ask
            them to change it after first login (their name menu → Password):
          </div>
          <code
            className="mono"
            style={{ fontSize: 16, padding: "10px 12px", userSelect: "all", display: "block" }}
          >
            {created.initialPassword}
          </code>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      title="New user"
      sub="the initial password is generated for you"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={create.isPending || !name.trim() || !email.trim()}
            onClick={submit}
          >
            {create.isPending ? "Creating…" : "Create user"}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name">
          <input
            className="field"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Jane Smith"
          />
        </Field>
        <Field label="Email">
          <input
            className="field"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="jane@multibot.ai"
          />
        </Field>
      </div>
    </Modal>
  );
}

export function UsersPage() {
  const toast = useToast();
  const me = useMe();
  const users = useUsers();
  const setActive = useSetUserActive();
  const [creating, setCreating] = useState(false);

  const items = users.data ?? [];

  return (
    <div className="stack">
      <Panel>
        <PanelHead
          title="Users"
          sub="anyone listed here can sign in and manage users — disabling also ends their sessions"
          actions={
            <Button variant="primary" onClick={() => setCreating(true)}>
              New user
            </Button>
          }
        />
        {users.isError ? (
          <PanelBody>
            <EmptyState title="Could not load users" hint={errorMessage(users.error)} />
          </PanelBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Created</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((user) => {
                const isSelf = user.id === me.data?.id;
                return (
                  <tr key={user.id}>
                    <td>
                      {user.name}
                      {isSelf ? <span className="sub"> (you)</span> : null}
                    </td>
                    <td className="mono">{user.email}</td>
                    <td>
                      <Pill tone={user.isActive ? "ok" : "danger"}>
                        {user.isActive ? "active" : "disabled"}
                      </Pill>
                    </td>
                    <td className="sub">{formatDateTime(user.createdAt)}</td>
                    <td>
                      {!isSelf && (
                        <Button
                          variant={user.isActive ? "danger" : "ghost"}
                          disabled={setActive.isPending}
                          onClick={() =>
                            setActive.mutate(
                              { id: user.id, isActive: !user.isActive },
                              {
                                onError: (error) =>
                                  toast.push({ message: errorMessage(error), tone: "danger" }),
                              },
                            )
                          }
                        >
                          {user.isActive ? "Disable" : "Enable"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </Panel>
      {creating && <CreateUserModal onClose={() => setCreating(false)} />}
    </div>
  );
}
